import React from "react";
import { useTranslation } from "react-i18next";
import { useFormContext, useFieldArray, Controller } from "react-hook-form";
import classNames from "classnames";

import { Button } from "../../../components/Button/Button";
import { TextInput } from "../../../components/forms/TextInput/TextInput";
import { Select } from "../../../components/forms/Select/Select";
import { Toggle } from "../../../components/forms/Toggle/Toggle";
import { Collapse } from "../../../components/Collapse/Collapse";

interface EntryFieldsSectionProps {
  isLocked: boolean;
  isOpen: boolean;
  onToggle: () => void;
}

export const EntryFieldsSection: React.FC<EntryFieldsSectionProps> = ({
  isLocked,
  isOpen,
  onToggle,
}) => {
  const { t } = useTranslation();
  const { register, control, setValue } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "settings.entryAdditionalFields",
  });

  return (
    <Collapse
      title={
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            {isLocked && <span className="icon-[ph--lock] text-[#877369] text-base" />}
            <span
              className={classNames(
                "text-lg font-bold font-header",
                isLocked ? "text-[#877369]" : "text-[#6c2f00]",
              )}
            >
              {t("Pages:StyleCreator.EntryFields.Title", "6. Entry form fields")}
            </span>
          </div>
          {!isLocked && (
            <span className="text-xs text-[#54433a] font-normal font-sans">
              {t(
                "Pages:StyleCreator.EntryFields.Subtitle",
                "Define additional fields that participants will complete.",
              )}
            </span>
          )}
        </div>
      }
      isOpen={isOpen}
      onToggle={onToggle}
      variant="plain"
      density="comfortable"
      className={classNames(
        "rounded-xl border border-solid transition-all duration-200",
        isLocked ? "opacity-60 cursor-not-allowed" : "",
      )}
      headerClassName={isLocked ? "pointer-events-none" : ""}
    >
      <fieldset disabled={isLocked} className="space-y-6 w-full">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="text-xs font-bold uppercase text-[#877369] border-b border-[#dac2b6] pb-4">
                <th className="pb-4 pr-4">
                  {t(
                    "Pages:StyleCreator.EntryFields.FieldName",
                    "Field Name",
                  )}
                </th>
                <th className="pb-4 px-4">
                  {t("Pages:StyleCreator.EntryFields.Type", "Type")}
                </th>
                <th className="pb-4 px-4 text-center">
                  {t("Pages:StyleCreator.EntryFields.Required", "Required")}
                </th>
                <th className="pb-4 px-4 text-center">
                  {t(
                    "Pages:StyleCreator.EntryFields.VisibleToJudges",
                    "Visible to judges",
                  )}
                </th>
                <th className="pb-4 px-4 text-center">
                  {t(
                    "Pages:StyleCreator.EntryFields.VisibleOnLabel",
                    "Visible on label",
                  )}
                </th>
                <th className="pb-4 pl-4 text-right">
                  {t("Pages:StyleCreator.EntryFields.Actions", "Actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#dac2b6] text-sm text-[#221a14]">
              {fields.length > 0 ? (
                fields.map((field, index) => (
                  <tr key={field.id} className="hover:bg-[#fff1e9]/30">
                    <td className="py-4 pr-4 min-w-[200px]">
                      <div className="flex items-center gap-2">
                        <span className="icon-[ph--dots-six-vertical] text-[#877369] cursor-move text-lg" />
                        <TextInput
                          containerClassName="w-full"
                          {...register(
                            `settings.entryAdditionalFields.${index}.label` as any,
                          )}
                          onChange={(e) => {
                            const val = e.target.value;
                            setValue(
                              `settings.entryAdditionalFields.${index}.label` as any,
                              val,
                            );
                            // Set key ID automatically
                            const key = val
                              .toLowerCase()
                              .replace(/[^a-z0-9]+/g, "_")
                              .replace(/(^_+|_+$)/g, "");
                            setValue(
                              `settings.entryAdditionalFields.${index}.key` as any,
                              key,
                            );
                          }}
                        />
                      </div>
                    </td>
                    <td className="py-4 px-4 min-w-[150px]">
                      <Controller
                        control={control}
                        name={`settings.entryAdditionalFields.${index}.fieldType`}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <Select
                            id={`settings.entryAdditionalFields.${index}.fieldType`}
                            options={[
                              {
                                label: t("EntryFieldType.Text", "Text"),
                                value: "text",
                              },
                              {
                                label: t("EntryFieldType.Number", "Numeric"),
                                value: "number",
                              },
                              {
                                label: t(
                                  "EntryFieldType.Boolean",
                                  "Boolean (Yes/No)",
                                ),
                                value: "boolean",
                              },
                            ]}
                            onChange={onChange}
                            onBlur={onBlur}
                            selected={value || "text"}
                          />
                        )}
                      />
                    </td>
                    <td className="py-4 px-4 text-center">
                      <div className="flex justify-center">
                        <Controller
                          control={control}
                          name={`settings.entryAdditionalFields.${index}.required`}
                          render={({ field: { onChange, value } }) => (
                            <Toggle value={!!value} onToggle={onChange} />
                          )}
                        />
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <div className="flex justify-center">
                        <Controller
                          control={control}
                          name={`settings.entryAdditionalFields.${index}.shownToJudges`}
                          render={({ field: { onChange, value } }) => (
                            <Toggle value={!!value} onToggle={onChange} />
                          )}
                        />
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <div className="flex justify-center">
                        <Controller
                          control={control}
                          name={`settings.entryAdditionalFields.${index}.shownOnLabel`}
                          render={({ field: { onChange, value } }) => (
                            <Toggle value={!!value} onToggle={onChange} />
                          )}
                        />
                      </div>
                    </td>
                    <td className="py-4 pl-4 text-right">
                      <Button
                        htmlType="button"
                        type="base"
                        variant="ghost"
                        onClick={() => remove(index)}
                        className="text-[#877369] hover:text-[#ba1a1a] hover:bg-[#ffdad6] p-1.5 rounded-lg"
                      >
                        <span className="icon-[ph--trash] text-lg" />
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="py-8 text-center text-xs text-[#877369] italic"
                  >
                    {t(
                      "Pages:StyleCreator.EntryFields.Empty",
                      "No fields configured. Click below to add.",
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex gap-4">
          <Button
            htmlType="button"
            type="primary"
            variant="filled"
            onClick={() =>
              append({
                key: "used_additives",
                label: t(
                  "Pages:StyleCreator.Presets.Additives",
                  "Used additives",
                ),
                fieldType: "text",
                required: false,
                shownToJudges: true,
                shownOnLabel: true,
              })
            }
            className="border border-[#904d00] text-[#904d00] hover:bg-[#fff1e9]/50 flex items-center gap-1.5 text-xs font-bold py-2 px-4 rounded-lg"
          >
            <span className="icon-[ph--plus-circle] text-sm" />
            {t(
              "Pages:StyleCreator.EntryFields.AddPreset",
              "Add preset field",
            )}
          </Button>

          <Button
            htmlType="button"
            type="secondary"
            variant="filled"
            onClick={() =>
              append({
                key: "",
                label: "",
                fieldType: "text",
                required: false,
                shownToJudges: false,
                shownOnLabel: false,
              })
            }
            className="border border-[#904d00] text-[#904d00] hover:bg-[#fff1e9]/50 flex items-center gap-1.5 text-xs font-bold py-2 px-4 rounded-lg"
          >
            <span className="icon-[ph--plus] text-sm" />
            {t("Pages:StyleCreator.EntryFields.AddCustom", "Add custom field")}
          </Button>
        </div>
      </fieldset>
    </Collapse>
  );
};
